import { Component, Input, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { buildRangedTimeOptions, isValidTime } from './date-time-field.util';

@Component({
  selector: 'lib-time-field',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeFieldComponent),
      multi: true,
    },
  ],
  template: `
    <select
      [id]="inputId"
      class="time-field"
      [attr.aria-label]="ariaLabel"
      [value]="timeText"
      [disabled]="isDisabled"
      (change)="onTimeChange($event)"
      (blur)="markAsTouched()"
    >
      <option value="">--:--</option>
      @for (option of timeOptions; track option) {
        <option [value]="option">{{ option }}</option>
      }
    </select>
  `,
  styles: [`
    :host {
      display: block;
      min-width: 0;
    }

    .time-field {
      width: 100%;
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 12px;
      font: inherit;
      color: #111827;
      background: #ffffff;
      box-sizing: border-box;
    }

    .time-field:disabled {
      opacity: 0.65;
      cursor: default;
    }
  `],
})
export class TimeFieldComponent implements ControlValueAccessor, OnChanges {
  @Input() public inputId = `time-field-${Math.random().toString(36).slice(2, 10)}`;
  @Input() public ariaLabel = 'Time';
  @Input() public minTime: string | null = null;
  @Input() public maxTime: string | null = null;
  @Input() public set timeStepMinutes(value: number) {
    const normalized = Math.trunc(value);
    this.timeStep = normalized > 0 && normalized <= 60 ? normalized : 15;
    this.baseTimeOptions = this.buildTimeOptions();
  }

  protected timeText = '';
  protected isDisabled = false;
  protected timeStep = 15;
  protected baseTimeOptions = this.buildTimeOptions();

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  protected get timeOptions(): string[] {
    const filtered = this.baseTimeOptions.filter((option) => this.isTimeAllowed(option));

    if (!this.timeText || filtered.includes(this.timeText)) {
      return filtered;
    }

    return [...filtered, this.timeText].sort();
  }

  public writeValue(value: string | null | undefined): void {
    const source = value?.trim() ?? '';
    this.timeText = isValidTime(source) ? source : '';
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['minTime'] || changes['maxTime']) {
      this.baseTimeOptions = this.buildTimeOptions();
    }
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  protected onTimeChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    this.timeText = target?.value ?? '';
    this.markAsTouched();
    this.onChange(this.timeText);
  }

  protected markAsTouched(): void {
    this.onTouched();
  }

  private isTimeAllowed(value: string): boolean {
    if (this.minTime && value < this.minTime) {
      return false;
    }

    if (this.maxTime && value > this.maxTime) {
      return false;
    }

    return true;
  }

  private buildTimeOptions(): string[] {
    return buildRangedTimeOptions(this.timeStep, this.minTime, this.maxTime);
  }
}
